import React, { useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import {
  X,
  CheckSquare,
  FileText,
  WalletCards,
  Flame,
  Target,
  Bookmark,
  Plus,
  Landmark,
  Building2,
} from 'lucide-react';

interface QuickAddModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const QuickAddModal: React.FC<QuickAddModalProps> = ({ isOpen, onClose }) => {
  const { setActiveModal, setCurrentPage, language } = useApp();
  const isBn = language === 'bn';

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleAction = (type: string) => {
    onClose();
    if (type === 'task') {
      setActiveModal('task');
    } else if (type === 'note') {
      setActiveModal('note');
    } else if (type === 'habit') {
      setActiveModal('habit');
    } else if (type === 'finance') {
      setCurrentPage('finance');
    } else if (type === 'goals') {
      setCurrentPage('goals');
    } else if (type === 'resources') {
      setCurrentPage('resources');
    }
  };

  const actions = isBn
    ? [
        {
          id: 'task',
          title: 'নতুন টাস্ক',
          description: 'আজকের বা ভবিষ্যতের যেকোনো কাজের তালিকা',
          icon: CheckSquare,
          color: 'text-blue-500 bg-blue-500/10 border-blue-500/30',
          btnBg: 'hover:border-blue-500',
        },
        {
          id: 'note',
          title: 'নতুন নোট',
          description: 'টেক্সট নোট, পিন লক ও ক্যালেন্ডার রিমাইন্ডার',
          icon: FileText,
          color: 'text-purple-500 bg-purple-500/10 border-purple-500/30',
          btnBg: 'hover:border-purple-500',
        },
        {
          id: 'finance',
          title: 'মাসিক আয় / ব্যয় এন্ট্রি',
          description: 'সাংগঠনিক, ব্যক্তিগত বা বিজনেস স্প্রেডশিট হিসাব',
          icon: WalletCards,
          color: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/30',
          btnBg: 'hover:border-emerald-500',
        },
        {
          id: 'habit',
          title: 'নতুন হ্যাবিট',
          description: 'দৈনিক বা সাপ্তাহিক রুটিন ও স্ট্রিক ট্র্যাকিং',
          icon: Flame,
          color: 'text-amber-500 bg-amber-500/10 border-amber-500/30',
          btnBg: 'hover:border-amber-500',
        },
        {
          id: 'goals',
          title: 'গোল বা প্ল্যান',
          description: 'বাৎসরিক লক্ষ্য, পরিকল্পনা ও বিস্তারিত বিবরণ',
          icon: Target,
          color: 'text-teal-500 bg-teal-500/10 border-teal-500/30',
          btnBg: 'hover:border-teal-500',
        },
        {
          id: 'resources',
          title: 'রিসোর্স লিংক',
          description: 'দরকারি ওয়েব লিংক ও বুকমার্ক সংরক্ষণ',
          icon: Bookmark,
          color: 'text-cyan-500 bg-cyan-500/10 border-cyan-500/30',
          btnBg: 'hover:border-cyan-500',
        },
      ]
    : [
        {
          id: 'task',
          title: 'New Task',
          description: 'Today or upcoming daily to-do item',
          icon: CheckSquare,
          color: 'text-blue-500 bg-blue-500/10 border-blue-500/30',
          btnBg: 'hover:border-blue-500',
        },
        {
          id: 'note',
          title: 'New Note',
          description: 'Personal notes, security PIN lock & reminders',
          icon: FileText,
          color: 'text-purple-500 bg-purple-500/10 border-purple-500/30',
          btnBg: 'hover:border-purple-500',
        },
        {
          id: 'finance',
          title: 'Finance & Ledger Entry',
          description: 'Organization, Personal or Business ledger sheet',
          icon: WalletCards,
          color: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/30',
          btnBg: 'hover:border-emerald-500',
        },
        {
          id: 'habit',
          title: 'New Habit Routine',
          description: 'Daily routines and streak tracker',
          icon: Flame,
          color: 'text-amber-500 bg-amber-500/10 border-amber-500/30',
          btnBg: 'hover:border-amber-500',
        },
        {
          id: 'goals',
          title: 'Year Goal & Plan',
          description: 'Yearly roadmap, target and plans',
          icon: Target,
          color: 'text-teal-500 bg-teal-500/10 border-teal-500/30',
          btnBg: 'hover:border-teal-500',
        },
        {
          id: 'resources',
          title: 'Resource Link',
          description: 'Bookmark useful web links and documents',
          icon: Bookmark,
          color: 'text-cyan-500 bg-cyan-500/10 border-cyan-500/30',
          btnBg: 'hover:border-cyan-500',
        },
      ];

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
    >
      <div className="w-full max-w-xl bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center font-bold">
              <Plus className="w-5 h-5" />
            </div>
            <div>
              <h3 className={`text-base font-black text-slate-900 dark:text-white ${isBn ? 'font-bangla' : ''}`}>
                {isBn ? 'দ্রুত নতুন কিছু যোগ করুন (Quick Add)' : 'Quick Create & Add'}
              </h3>
              <p className="text-xs text-slate-500">
                {isBn ? 'নিচের অপশনগুলো থেকে আপনার প্রয়োজনীয়টি নির্বাচন করুন' : 'Select what you would like to create'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl bg-slate-100 dark:bg-slate-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Action Grid */}
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3.5 max-h-[70vh] overflow-y-auto">
          {actions.map((act) => {
            const Icon = act.icon;
            return (
              <button
                key={act.id}
                onClick={() => handleAction(act.id)}
                className={`p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40 hover:bg-white dark:hover:bg-[#1f2937] text-left transition-all group flex items-start gap-3.5 shadow-2xs hover:shadow-md ${act.btnBg}`}
              >
                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 border ${act.color} group-hover:scale-110 transition-transform`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h4 className={`text-xs font-black text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors ${isBn ? 'font-bangla' : ''}`}>
                    {act.title}
                  </h4>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed line-clamp-2">
                    {act.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
